import { Injectable } from '@angular/core'
import { BehaviorSubject } from 'rxjs'
import { SessionStorageService } from 'ngx-webstorage'
import { PlatformService } from './platform.service'

export interface ViewedUserInfo {
  email: string
  id: number
  name?: string
}

export interface OriginalUserTokens {
  jwt_token: string
  refresh_token: string
  saved_at: string
}

@Injectable({
  providedIn: 'root'
})
export class ViewAsService {
  private readonly ORIGINAL_USER_STORAGE_KEY = 'flo_original_user_tokens'
  private readonly VIEWED_USER_INFO_KEY = 'flo_viewed_user_info'

  private readonly _isViewingAsUser$ = new BehaviorSubject<boolean>(false)
  private readonly _viewedUserInfo$ = new BehaviorSubject<ViewedUserInfo | null>(null)

  readonly isViewingAsUser$ = this._isViewingAsUser$.asObservable()
  readonly viewedUserInfo$ = this._viewedUserInfo$.asObservable()

  constructor(private platformService: PlatformService, private sessionStorageService: SessionStorageService) {
    // Initialize state from sessionStorage on service creation (only on browser)
    this.initializeState()
  }

  private initializeState() {
    if (!this.platformService.isBrowser) {
      // On server, initialize with default values
      this._isViewingAsUser$.next(false)
      this._viewedUserInfo$.next(null)
      return
    }

    // Check sessionStorage for existing view-as session
    const viewedUserInfo = this.getViewedUserInfo()
    const hasOriginalTokens = this.hasOriginalUserTokens()

    // Only consider viewing-as if both user info and original tokens exist
    const isViewingAs = !!(viewedUserInfo && hasOriginalTokens)

    this._isViewingAsUser$.next(isViewingAs)
    this._viewedUserInfo$.next(isViewingAs ? viewedUserInfo : null)
  }

  setViewingAsUser(userInfo: ViewedUserInfo) {
    this.saveViewedUserInfo(userInfo)
    this._isViewingAsUser$.next(true)
    this._viewedUserInfo$.next(userInfo)
  }

  clearViewingAsUser() {
    this.clearViewedUserInfo()
    this.clearOriginalUserTokens() // Clear admin tokens from sessionStorage
    this._isViewingAsUser$.next(false)
    this._viewedUserInfo$.next(null)
  }

  saveOriginalUserTokens(tokenData: OriginalUserTokens): void {
    if (this.platformService.isBrowser) {
      this.sessionStorageService.store(this.ORIGINAL_USER_STORAGE_KEY, tokenData)
    }
  }

  getOriginalUserTokens(): OriginalUserTokens | null {
    if (!this.platformService.isBrowser) {
      return null
    }
    return this.sessionStorageService.retrieve(this.ORIGINAL_USER_STORAGE_KEY) || null
  }

  private saveViewedUserInfo(userInfo: ViewedUserInfo) {
    if (this.platformService.isBrowser) {
      this.sessionStorageService.store(this.VIEWED_USER_INFO_KEY, userInfo)
    }
  }

  private getViewedUserInfo(): ViewedUserInfo | null {
    if (!this.platformService.isBrowser) {
      return null
    }
    return this.sessionStorageService.retrieve(this.VIEWED_USER_INFO_KEY) || null
  }

  private clearViewedUserInfo() {
    if (this.platformService.isBrowser) {
      this.sessionStorageService.clear(this.VIEWED_USER_INFO_KEY)
    }
  }

  private hasOriginalUserTokens(): boolean {
    if (!this.platformService.isBrowser) {
      return false
    }
    return !!this.sessionStorageService.retrieve(this.ORIGINAL_USER_STORAGE_KEY)
  }

  clearOriginalUserTokens() {
    if (this.platformService.isBrowser) {
      this.sessionStorageService.clear(this.ORIGINAL_USER_STORAGE_KEY)
    }
  }
}
